/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button } from 'reactstrap';
import Logo from '../../images/logo.svg';
import { Loader } from '@g17eco/atoms/loader';
import { Footer } from '@g17eco/molecules/footer';
import { loadOnboardingToken, reloadOnboardingToken } from '@actions/onboardingToken';
import * as auth from '@actions/auth';
import { loadCurrentUser } from '@actions/user';
import UserForm from './forms/user';
import SurveyForm from './forms/survey';
import Error from './forms/error';
import ActivationRequired from './forms/ActivationRequired';
import { showLogin } from '@actions/login';
import { ROUTES } from '@constants/routes';
import { generateUrl } from '../util';
import { getAnalytics } from '@services/analytics/AnalyticsService';
import { AnalyticsEvents } from '@services/analytics/AnalyticsEvents';
import { OnboardingStatus, OnboardingToken } from '@g17eco/types/onboarding';
import { useAppDispatch, useAppSelector } from '@reducers/index';
import { getOnboardingAppPath } from '@selectors/appConfig';
import { hasAssuranceConfig, isAssuranceTrackerOnboarding, isMaterialityTrackerOnboarding } from '@utils/onboarding';
import { useLoadCommonDataQuery } from '@api/common';
import { BasicAlert } from '@g17eco/molecules/alert';
import './styles.scss';

enum OnboardingForm {
  UserForm = 'user',
  UserComplete = 'user_complete',
  SurveyForm = 'survey',
  Error = 'error',
}

const Onboarding = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const token = new URLSearchParams(location.search).get('invitation_token') as string;

  const onboardingTokenState = useAppSelector((state) => state.onboardingToken);
  const { data: onboardingToken } = onboardingTokenState;
  const currentUserState = useAppSelector((state) => state.currentUser);
  const { data: currentUser, loaded: loggedIn } = currentUserState;
  const rootAppPath = useAppSelector(getOnboardingAppPath);

  const [userMessage, setUserMessage] = useState<string>();

  const reloadOnboarding = () => dispatch(reloadOnboardingToken(token));
  const handleLogout = () => dispatch(auth.logout());

  const { data: commonData, isFetching: isFetchingCommon } = useLoadCommonDataQuery();
  const countries = commonData?.countries ?? [];

  useEffect(() => {
    dispatch(loadCurrentUser());
  }, [dispatch]);

  useEffect(() => {
    dispatch(loadOnboardingToken(token));
  }, [dispatch, currentUser._id, token]);

  const getForm = () => {
    if (onboardingTokenState.errored || !onboardingTokenState.loaded) {
      return OnboardingForm.Error;
    }

    if (onboardingToken.status === OnboardingStatus.Complete) {
      return OnboardingForm.UserComplete;
    }

    const onboardUser = onboardingToken.user;
    const userOnboardingRequired = !loggedIn && onboardUser && !onboardUser.complete;

    if (userOnboardingRequired) {
      return OnboardingForm.UserForm;
    }

    const isSurveyOnboardingRequired =
      (onboardingToken.surveyConfig && !onboardingToken.surveyConfig.complete) ||
      onboardingToken.surveyStakeholders ||
      onboardingToken.utrvConfig ||
      onboardingToken.assuranceStakeholders ||
      onboardingToken.surveyRoles ||
      onboardingToken.delegationScope;

    if (isSurveyOnboardingRequired) {
      return OnboardingForm.SurveyForm;
    }

    if (onboardUser && onboardUser.complete) {
      return OnboardingForm.UserComplete;
    }

    return OnboardingForm.Error;
  };

  const onboardingForm = getForm();

  useEffect(() => {
    if (onboardingForm === OnboardingForm.UserComplete && currentUser.active === true) {
      if (hasAssuranceConfig(onboardingToken) || isAssuranceTrackerOnboarding(onboardingToken)) {
        // Go to assurance as priority
        return history.push(generateUrl(ROUTES.ASSURANCE));
      }

      if (isMaterialityTrackerOnboarding(onboardingToken)) {
        return history.push(generateUrl(ROUTES.MATERIALITY_TRACKER));
      }

      if (onboardingToken.initiativeId) {
        return history.push(generateUrl(ROUTES.SUMMARY, { rootAppPath, initiativeId: onboardingToken.initiativeId }));
      }

      return history.push(generateUrl(ROUTES.COMPANY_TRACKER, { rootAppPath }));
    }

    if (onboardingToken._id) {
      const onboardUser = (onboardingToken as OnboardingToken).user;
      if (!onboardUser.complete) {
        const analytics = getAnalytics();
        analytics.track(AnalyticsEvents.UserInvitationClick, {
          initiativeId: onboardingToken.initiativeId,
          onboardId: onboardingToken._id,
        });
      }
    }
  }, [history, currentUser, onboardingToken, onboardingForm, rootAppPath]);

  const renderLoggedInMessage = () => {
    if (!loggedIn) {
      return;
    }

    return (
      <div className='row'>
        <div className='offset-3 col-6'>
          <p>
            You are currently logged in as{' '}
            <strong>
              {currentUser.firstName} {currentUser.surname}
            </strong>
            . To use a different user{' '}
            <span className={'ml-2 link-like text-nowrap'} onClick={handleLogout}>
              {' '}
              click here.
            </span>
          </p>
        </div>
      </div>
    );
  };

  if (onboardingTokenState.loading || currentUserState.loading || isFetchingCommon) {
    return <Loader />;
  }

  const loginRequired = () => {
    switch (onboardingForm) {
      case OnboardingForm.UserForm:
      case OnboardingForm.Error:
        return false;
      default:
        return true;
    }
  }

  const onLogin = (path?: string) => {
    const { pathname, search } = location;
    return showLogin(path ?? pathname + search);
  };

  const renderForm = () => {
    const status = onboardingToken.status === OnboardingStatus.Complete ? 'has been completed' : 'is pending';

    if (!loggedIn && loginRequired()) {
      return (
        <div className='mt-5 row'>
          <div className='offset-3 col-6'>
            <h1>Welcome!</h1>
            <p>This invitation {status}. Please log in using the button below.</p>
            <div className='text-right'>
              <Button
                type='button'
                onClick={() => onLogin()}
                name='action'
                value='show'
                color='secondary'
                className='w-100'
              >
                {onboardingToken.externalIdp ? 'Please login with your SSO provider' : 'Log in'}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (onboardingForm === OnboardingForm.UserForm) {
      return (
        <UserForm
          reloadOnboarding={reloadOnboarding}
          countries={countries}
          token={onboardingToken as OnboardingToken}
          setUserMessage={(message) => setUserMessage(message)}
          showLogin={onLogin}
        />
      );
    }

    if (onboardingForm === OnboardingForm.SurveyForm && currentUser && onboardingTokenState.loaded) {
      return <SurveyForm reloadOnboarding={reloadOnboarding} token={onboardingTokenState.data} setUserMessage={setUserMessage} />;
    }

    if (onboardingForm === OnboardingForm.UserComplete && !currentUser.active) {
      return <ActivationRequired />;
    }

    if (onboardingForm === OnboardingForm.Error) {
      return (
        <Error
          reloadOnboarding={reloadOnboarding}
          errorMessage={onboardingTokenState.errorMessage}
          token={onboardingToken}
          setUserMessage={setUserMessage} />
      );
    }

    return null;
  };

  return (
    <div className='onboardingWrapper'>
      <div className='onboardingContainer'>
        <header>
          <img className='logo' src={Logo} alt='G17Eco' />
        </header>

        <div className='onboardingBodyContainer'>
          {renderLoggedInMessage()}
          {userMessage ? (
            <div className='row mt-3'>
              <div className='offset-3 col-6'>
                <BasicAlert type='primary'>{userMessage}</BasicAlert>
              </div>
            </div>
          ) : null}
          {renderForm()}
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default Onboarding;
